import React, { useContext, useState, useEffect, useMemo, useRef } from "react";
import { Row, Col, Button, Form, Container } from "react-bootstrap";
import { CurrentAgingForm } from "./CurrentAgingForm";
import ErrorHandler from "../../../components/core/ErrorHandler";

import { DetailsModal } from "../../../components/grid/DetailsModal";
import { IgnoreModal } from "shared/components/ignoreModal/IgnoreModal";
import { IgnoreBody } from "shared/components/ignoreModal/IgnoreBody";
import Accordion from "../../../components/Accordion";
//NOTE TODO: I think this is needed in some of the "PresentData..." functions,
// there's a recent ticket that seems to be about "refixing" an issue related to dates being one off
//import { GetDataWithCleanedDates } from "shared/helpers/DateHelpers.js";

import SharedMainSelectableClientGrid from "shared/components/grids/clientGrid/SharedMainSelectableClientGrid";
import SharedMainSelectableServerGrid from "shared/components/grids/serverGrid/SharedMainSelectableServerGrid";
import { useInsertServerSideExport } from "shared/hooks/useInsertServerSideExport";
import { UserContext } from "shared/context/UserContext";

import { IsNullOrEmpty } from "shared/helpers/NullEmptyHelpers";

import { Grid, GridColumn, GridToolbar } from "@progress/kendo-react-grid";

import { getter, ZIndexContext } from "@progress/kendo-react-common";
import { LogError } from "../../../components/AppInsights/TelemetryService";

import useGridButtons from "shared/components/grids/hooks/useGridButtons";

import {
  MoneyCell,
  AggregateMoneyFooterCell,
} from "../../../components/grid/Kendo/CustomKendoGridCells";

import { PromiseToPayHeaderCell } from "./components/PromiseToPayHeaderCell";

import {
  DefaultColumnWidth,
  DefaultDateColumnWidth,
  DefaultBooleanOrShortWidthColumnWidth,
} from "../../../components/grid/Kendo/KendoGridAndColumnConstants";

import useGridDataStateResetProcessing from "shared/hooks/useGridDataStateResetProcessing";
import { useReactQueryHelpers } from "shared/hooks/useReactQueryHelpers";
import ShouldDisplayDetailsModal from "./ShouldDisplayDetailsModalHelper";
import { ConstructAgingPeriodAggregateSettingsHelper } from "./helpers/ConstructAgingPeriodAggregateSettingsHelper";
import { SetSummaryDetailsDataHelper } from "./helpers/SetSummaryDetailsDataHelper";
import { ParsePayerSetupStatusFilter } from "./helpers/ParsePayerSetupStatusFilter";

export function CurrentAging(props) {
  const { passedProps } = props;
  const user = passedProps.user.email;
  const agingPeriods = passedProps.agingPeriods;

  const { invalidateReactQueryKey } = useReactQueryHelpers();

  const clientGridRef = useRef();
  const agingDetailsServerGridRef = useRef();

  const userContext = useContext(UserContext);
  const DETAILS_DATA_ITEM_KEY = "claimId";
  const [submittedFormData, setSubmittedFormData] = useState({});
  const [showFilter, setShowFilter] = useState("");
  const [payerSetupStatusFilter, setPayerSetupStatusFilter] = useState("");

  const [error, setError] = useState({});

  const [detailError, setDetailError] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [showIgnoreCount, setShowIgnoreCount] = useState(false);
  const [showIgnoreModal, setShowIgnoreModal] = useState(false);
  const [isIgnoreClaimsFormDisabled, setIsIgnoreClaimsFormDisabled] =
    useState(false);
  const [IgnoreError, setIgnoreError] = useState({});

  const [selectedNcpdpIgnoreModalText, setSelectedNcpdpIgnoreModalText] =
    useState("");
  const [selectedRxIgnoreModalText, setSelectedRxIgnoreModalText] =
    useState("");
  const [selectedClid, setSelectedClid] = useState("");
  //NOTE: This is for holding the "set of parameters used when the details call is made"
  //this is needed so that we can re-fetch the data following an ignore action
  const [
    selectedDetailsPropsForRefetchAfterIgnore,
    setSelectedDetailsPropsForRefetchAfterIgnore,
  ] = useState("");

  const [
    ignoreMultipleSelectedClaimsInDetailModal,
    setIgnoreMultipleSelectedClaimsInDetailModal,
  ] = useState(false);

  const {
    gridDataStateResetProcessingInitiated,
    InitiateGridDataStateReset,
    GridDataStateResetCompleted: onGridDataStateResetCompleted,
  } = useGridDataStateResetProcessing();

  /*
  NOTE: mainGridColumns is necessary because pharmacies can have different aging periods.
  Different aging periods means different column headers.
  */
  const [mainGridColumns, setMainGridColumns] = useState([]);
  /*
  NOTE: mainGridAggregates is still necessary here because
  although the composition of the aggregates is handled by the shared grid component,
  the display of the values is still handled on a cell level.
  */
  const [mainGridAggregates, setMainGridAggregates] = useState([]);

  const [detailsGridApiEndpoint, setDetailsGridApiEndpoint] = useState(null);
  const [detailsGridApiPostEndpoint, setDetailsGridApiPostEndpoint] = useState(null);
  const [detailsGridParameters, setDetailsGridParameters] = useState({});
  const [refreshDetailGrid, setRefreshDetailGrid] = useState(false);

  const [detailsGridTitle, setDetailsGridTitle] = useState("");
  const [summaryDetailsGridData, setSummaryDetailsGridData] = useState(null);

  const { AddGridButton, gridButtons } = useGridButtons();

  const _container = React.useRef();

  const sendAggregatesToParent = (aggregatesFromSharedMainAgingGrid) => {
    setMainGridAggregates(aggregatesFromSharedMainAgingGrid.data);
  };

  function handleFormSubmit(e) {
    //NOTE: Reset the column and aggregate settings each time the user makes a new search
    setMainGridColumns([]);
    setMainGridAggregates([]);
    
    // Clear any existing error message when initiating a new search
    setError({});

    setSubmittedFormData(e);

    InitiateGridDataStateReset();
  }

  //set the grid buttons
  useEffect(() => {
    AddGridButton(
      "Ignore Selected Claims",
      handleIgnoreMultipleSelectedClaims,
      true
    );
  }, []);

  const currentAgingApiParams = useMemo(() => {
    if (IsNullOrEmpty(userContext.email)) {
      return {};
    }

    if (IsNullOrEmpty(submittedFormData.mmid)) {
      return {};
    }

    let params = {
      user: userContext.email,
      mmids: submittedFormData.mmid,
      dateType: submittedFormData.dateType,
      reportType: submittedFormData.reportType,
      showPayerType: submittedFormData.showPayerType || "",
    };

    /*
    NOTE: "Show All" must reach the API as "no value at all" so the stored procedures' nullable
    @PayerSetupStatus parameter stays NULL. The helper returns undefined in that case, which makes
    axios drop the key from the query string entirely.
    */
    let payerSetupStatus = ParsePayerSetupStatusFilter(
      submittedFormData.payerSetupStatus
    );
    if (payerSetupStatus !== undefined) {
      params.payerSetupStatus = payerSetupStatus;
    }

    return params;
  }, [submittedFormData]);

  let CustomSetupCell = (props) => {
    let cellValue = props.dataItem[props.field];

    return (
      <td
        {...props.tdProps}
        colSpan={1}
        style={{
          color: cellValue ? "#008000" : "#ff0000",
          fontSize: "medium",
        }}
      >
        <i
          className={cellValue ? "fa fa-check-circle" : "fa fa-times-circle"}
        />
      </td>
    );
  };

  function getCurrentAgingDetails(props) {
    setShowModal(true);
    setDetailError({});

    /*
    NOTE: The drill-downs must honor the same setup status filter as the summary grid, otherwise a
    filtered cell opens an unfiltered popup and the totals will not reconcile. The same value also
    rides along into the serverside Excel exports, which reuse these parameters.
    */
    const payerSetupStatus = ParsePayerSetupStatusFilter(
      submittedFormData.payerSetupStatus
    );

    if (props.isAggregate) {
      if (props.dataItem.agingDateRange === "TotalOutstanding") {
        setDetailsGridTitle("Total Outstanding Aggregate");
      } else {
        setDetailsGridTitle(
          props.dataItem.agingDateRange + " Days Old Aggregate"
        );
      }

      // Use POST endpoint for aggregate data with proper Report structure
      setDetailsGridApiEndpoint(null);
      setDetailsGridApiPostEndpoint(
        "api/AgingDetails/PostByCurrentAgingAggregatesServerGrid"
      );
      
      // Structure parameters for POST endpoint with Report wrapper
      const postParameters = {
        Report: {
          User: props.dataItem.user || user,
          Mmids: props.dataItem.mmids || submittedFormData.mmid,
          OutstandingType: props.dataItem.outstandingType || submittedFormData.reportType,
          DateType: props.dataItem.dateType || submittedFormData.dateType,
          AgingDateRange: props.dataItem.agingDateRange,
          FilteredBins: props.dataItem.filteredBins || "",
          FilteredPbids: props.dataItem.filteredPbids || "",
        }
      };

      if (payerSetupStatus !== undefined) {
        postParameters.Report.PayerSetupStatus = payerSetupStatus;
      }
      
      setDetailsGridParameters(postParameters);
    } else {
      //NOTE: I wonder if there's a way to delay showing the modal until the data is returned
      if (props.field === "TotalOutstanding") {
        setDetailsGridTitle(
          "BIN - " +
            props.dataItem["Bin"] +
            ", " +
            props.dataItem["Payer"] +
            " Total Outstanding"
        );
      } else {
        setDetailsGridTitle(
          "BIN - " +
            props.dataItem["Bin"] +
            ", " +
            props.dataItem["Payer"] +
            ", " +
            props.field +
            " Days Old"
        );
      }

      if (props.field === "TotalOutstanding") {
        setDetailsGridTitle(
          "BIN - " +
            props.dataItem["Bin"] +
            ", " +
            props.dataItem["Payer"] +
            " Total Outstanding"
        );
      } else {
        setDetailsGridTitle(
          "BIN - " +
            props.dataItem["Bin"] +
            ", " +
            props.dataItem["Payer"] +
            ", " +
            props.field +
            " Days Old"
        );
      }

      let parameters1 = {
        user: user,
        mmids: submittedFormData.mmid,
        outstandingType: submittedFormData.reportType,
        dateType: submittedFormData.dateType,
        agingDateRange: props.field,
        bin: props.dataItem["Bin"],
        pbid: props.dataItem["PBID"],
      };

      if (payerSetupStatus !== undefined) {
        parameters1.payerSetupStatus = payerSetupStatus;
      }

      // Use GET endpoint for non-aggregate data (keeping existing behavior)
      setDetailsGridApiEndpoint("api/AgingDetails/GetByCurrentAgingServerGrid");
      setDetailsGridApiPostEndpoint(null);
      setDetailsGridParameters(parameters1);
    }
  }

  let MainGridMoneyCell = (props) => {
    let onCellClick = () => {
      let inputProps = {
        ...props,
        isAggregate: false,
      };
      setSelectedDetailsPropsForRefetchAfterIgnore(inputProps);
      getCurrentAgingDetails(inputProps);
    };

    let options = {
      onClick: onCellClick,
    };

    return MoneyCell(props, options);
  };

  async function aggregateMoneyFooterCellClick(aggregateField) {
    // Get all filtered data from all pages (respects current filters but includes all pages)
    const allFilteredDataFromComponent = clientGridRef.current
      ? clientGridRef.current.GetAllFilteredData()
      : [];

    let allFilteredBins = "";
    let allFilteredPBIDs = "";
    if (allFilteredDataFromComponent && allFilteredDataFromComponent.length > 0) {
      allFilteredDataFromComponent.forEach((element) => {
        allFilteredBins = allFilteredBins + element["Bin"] + ",";
        allFilteredPBIDs = allFilteredPBIDs + element["PBID"] + ",";
      });
    } else {
      alert("No data is displayed on grid");
      return null;
    }

    let parameters = {
      dataItem: {
        user: user,
        mmids: submittedFormData.mmid,
        outstandingType: submittedFormData.reportType,
        dateType: submittedFormData.dateType,
        agingDateRange: aggregateField,
        filteredBins: allFilteredBins,
        filteredPbids: allFilteredPBIDs,
      },
      isAggregate: true,
    };

    setSelectedDetailsPropsForRefetchAfterIgnore(parameters);
    getCurrentAgingDetails(parameters);
  }

  const AggregateMainGridMoneyFooterCell = (props) => {
    var options = {
      //NOTE: Setting "mainGridAggregates" is still necessary;
      //the aggregates may be defined by the central component, but the display is still handled by the cell
      aggregateData: mainGridAggregates,
      onClick: () => aggregateMoneyFooterCellClick(props.field),
    };

    return AggregateMoneyFooterCell(props, options);
  };

  let CustomIgnoreDetailCell = (props) => {
    return (
      <td
        {...props.tdProps}
        colSpan={1}
        style={{
          color: "#015CAB",
        }}
      >
        <a
          style={{
            cursor: "pointer",
          }}
          onClick={() => ignoreDetailCellClick(props)}
        >
          Ignore
        </a>
      </td>
    );
  };

  const AggregateMoneyDetailFooterCell = (props) => {
    if (!agingDetailsServerGridRef.current) {
      return (
        <td
          aria-colindex={props.tdProps["aria-colindex"]}
          className={props.tdProps.className}
          colSpan={props.tdProps.colSpan}
          role={props.tdProps.role}
          style={props.tdProps.style}
          key={props.tdProps.key}
        >
          {"$0.00"}
        </td>
      );
    } else {
      const gridData = agingDetailsServerGridRef.current
        ? agingDetailsServerGridRef.current.GetAllGridData()
        : null;

      if (gridData && gridData.fieldTotals) {
        let fieldTotals = gridData.fieldTotals;

        let options = {
          aggregateData: fieldTotals,
        };

        //NOTE: Okay so for some reason this is necessary
        let modifiedProps = {
          ...props,
        };
        modifiedProps.field = props.field.toLowerCase();

        return AggregateMoneyFooterCell(modifiedProps, options);
      }
    }
  };

  //NOTE: This is the NEW Ignore detail for the Kendo grid
  function ignoreDetailCellClick(props) {
    let dataRow = props.dataItem;

    setIgnoreMultipleSelectedClaimsInDetailModal(false);
    setSelectedNcpdpIgnoreModalText(dataRow.ncpdp);
    setSelectedRxIgnoreModalText(dataRow.rxNumber);
    setSelectedClid(dataRow.claimId);
    setShowIgnoreModal(true);
  }

  function handleIgnoreMultipleSelectedClaims() {
    const allDetailsClaims = agingDetailsServerGridRef.current
      ? agingDetailsServerGridRef.current.GetAllGridData()
      : null;

    let selectedDetailsCount = getSelectedDetailsCount();
    let detailsCount = allDetailsClaims.length;

    if (
      getSelectedDetailsCount() > 14 ||
      selectedDetailsCount === detailsCount
    ) {
      setShowIgnoreCount(true);
    } else {
      setShowIgnoreModal(true);
    }
    setIgnoreMultipleSelectedClaimsInDetailModal(true);
    setSelectedNcpdpIgnoreModalText("All Selected");
    setSelectedRxIgnoreModalText("All Selected");
  }

  function getSelectedDetailsCount() {
    const selectedClaims = agingDetailsServerGridRef.current
      ? agingDetailsServerGridRef.current.GetSelectedGridDataKeys()
      : null;

    if (selectedClaims) {
      let selectedDetailsCount = selectedClaims.length;
      return selectedDetailsCount;
    }

    return 0;
  }

  function handleCancelIgnoreClick() {
    setIsIgnoreClaimsFormDisabled(false);
    setShowIgnoreModal(false);
  }

  function handleIgnoreCountClick() {
    setShowIgnoreCount(false);
    setShowIgnoreModal(true);
  }

  const onIgnoreClaimsSaveButtonCallback = () => {
    setIsIgnoreClaimsFormDisabled(true);
  };

  const onIgnoreClaimSuccessCallback = (response) => {
    setShowIgnoreModal(false);

    setRefreshDetailGrid(refreshDetailGrid ? false : true);

    invalidateReactQueryKey("api/Aging/GetCurrentAging");
    invalidateReactQueryKey("api/AgingDetails/GetByCurrentAgingServerGrid");
    invalidateReactQueryKey(
      "api/AgingDetails/GetByCurrentAgingAggregatesServerGrid"
    );

    setIgnoreActionCompletedMessage();
    setIsIgnoreClaimsFormDisabled(false);
  };

  let clidsToIgnore = "";

  if (ignoreMultipleSelectedClaimsInDetailModal) {
    const selectedClaims = agingDetailsServerGridRef.current
      ? agingDetailsServerGridRef.current.GetSelectedGridDataKeys()
      : null;

    if (selectedClaims) {
      clidsToIgnore = selectedClaims.join(",");
    } else {
      clidsToIgnore = "";
    }
  } else {
    clidsToIgnore = selectedClid;
  }

  function setIgnoreActionCompletedMessage() {
    setDetailError({
      status: 200,
      Message: "Selected items have been ignored!",
    });
    setTimeout(() => {
      setDetailError({});
    }, 3600);
  }

  function onFailure(error) {
    LogError(error);

    switch (error.response.config.url || error.config.url) {
      case "api/Aging/GetCurrentAging":
        setError(error.response);
        setMainGridColumns([]);
        break;
      case "api/AgingDetails/GetByCurrentAging":
        //NOTE: Since we already have an error toast display on the grid, we can comment out this one
        // setDetailError(error.response);
        setShowModal(true);
        break;
      case "api/AgingDetails/GetByCurrentAgingServerGrid":
        //NOTE: Since we already have an error toast display on the grid, we can comment out this one
        // setDetailError(error.response);
        setShowModal(true);
        break;
      case "api/AgingDetails/GetByCurrentAgingAggregatesServerGrid":
        //NOTE: Since we already have an error toast display on the grid, we can comment out this one
        // setDetailError(error.response);
        setShowModal(true);
        break;
      case "api/AgingDetails/PostByCurrentAgingAggregatesServerGrid":
        //NOTE: Handle the new POST endpoint for aggregates
        // setDetailError(error.response);
        setShowModal(true);
        break;
      default:
        setError(error.response);
        setMainGridColumns([]);
        break;
    }
    setIsIgnoreClaimsFormDisabled(false);
    return;
  }

  function determineMainGridColumnsAndAggregateSettings(data) {
    if (mainGridColumns.length === 0) {
      let columns = [];

      if (data.length > 0) {
        let headerProps = {
          PBID: { title: "PBID", hidden: true },
          Payer: { title: "Payer Name", selectable: false },
          Bin: { title: "BIN", selectable: false },
          TotalOutstanding: {
            title: "Total Outstanding",
            type: "money",
            showTotal: true,
            selectable: true,
          },
          Setup: { title: "Setup", type: "bool" },
        };

        Object.keys(data[0]).map((key) => {
          columns.push({
            accessor: key,
            title: headerProps[key]
              ? headerProps[key].title
              : key.includes("+")
              ? `${key} days and over`
              : `${key} days`,
            type: headerProps[key] ? headerProps[key].type : "money",
            showTotal: headerProps[key] ? headerProps[key].showTotal : true,
            selectable: headerProps[key] ? headerProps[key].selectable : true,
            hidden: headerProps[key] ? headerProps[key].hidden : false,
          });
        });
        setError({});
      } else {
        setError({
          status: 201,
          Message: "No data found for given parameters",
        });
      }
      setMainGridColumns(columns);
    }
  }

  let { mutate: mutateForServerSideExport } = useInsertServerSideExport();

  // function detailGridErrorCallback(input) {
  //   console.log(input);
  //   console.log(selectedDetailsPropsForRefetchAfterIgnore);
  // }

  function detailGridErrorCallback(input) {
    onFailure(input);

    if (selectedDetailsPropsForRefetchAfterIgnore.isAggregate) {
      // The aggregate grid uses a POST endpoint, so the failed request's URL has no
      // query string to parse — pass the parameters directly (flattened to match
      // the export endpoint's flat model, not the Report wrapper).
      let mutateParameter = {
        error: input,
        endpoint:
          "api/AgingDetails/PostAggregateCurrentAgingDetailsExcelExportRequestToAzureStorage",
        directParameters: detailsGridParameters.Report
          ? { ...detailsGridParameters.Report, Skip: 0, Take: 0 }
          : detailsGridParameters,
      };

      mutateForServerSideExport(mutateParameter);
    } else {
      let mutateParameter = {
        error: input,
        endpoint:
          "api/AgingDetails/PostCurrentAgingDetailsExcelExportRequestToAzureStorage",
      };

      mutateForServerSideExport(mutateParameter);
    }
  }

  function CreateGridColumn(column) {
    let columnWidth = DefaultColumnWidth();
    if (column.accessor !== "PBID") {
      let filterType = "text";
      if (column.type === "money") {
        filterType = "numeric";
      } else if (column.type === "bool") {
        filterType = "boolean";
      }

      if (column.accessor === "Setup") {
        return (
          <GridColumn
            key={column.accessor}
            filter={filterType}
            field={column.accessor}
            title={column.title}
            width={columnWidth}
            cells={{
              data: CustomSetupCell,
            }}
          />
        );
      } else if (column.type === "money") {
        return (
          <GridColumn
            key={column.accessor}
            filter={filterType}
            field={column.accessor}
            title={column.title}
            width={columnWidth}
            cells={{
              data: MainGridMoneyCell,
              footerCell: AggregateMainGridMoneyFooterCell,
            }}
            footerCell={AggregateMainGridMoneyFooterCell}
          />
        );
      } else {
        return (
          <GridColumn
            key={column.accessor + "Column"}
            filter={filterType}
            field={column.accessor}
            title={column.title}
            width={columnWidth}
          />
        );
      }
    }
  }

  let detailsGridUseQueryOptions = { refetchOnWindowFocus: false };

  return (
    <div className="feature-tab" id="current-aging">
      <ErrorHandler
        error={error}
        onClose={() => {
          setError({});
        }}
      />
      <Accordion defaultExpanded label="Search & Filter">
        <Row>
          <Col className="higher-zindex-filters">
            <CurrentAgingForm
              handleFormSubmit={handleFormSubmit}
              showFilter={showFilter}
              onShowFilterChange={setShowFilter}
              payerSetupStatusFilter={payerSetupStatusFilter}
              onPayerSetupStatusFilterChange={setPayerSetupStatusFilter}
            />
          </Col>
        </Row>
      </Accordion>
      <div ref={_container}>
        <Row>
          <Col className="tight-grid multi-line-filter">
            <SharedMainSelectableClientGrid
              ref={clientGridRef}
              apiGetEndpoint={"api/Aging/GetCurrentAging"}
              apiParameters={currentAgingApiParams}
              aggregateColumnSettings={ConstructAgingPeriodAggregateSettingsHelper(
                agingPeriods
              )}
              sendAggregatesParentCallback={sendAggregatesToParent}
              styleOverride={{ minHeight: "19em", maxHeight: "50em" }}
              exportFileNamePrefix={"CurrentAging"}
              gridDataStateResetProcessingInitiated={
                gridDataStateResetProcessingInitiated
              }
              onGridDataStateResetCompleted={onGridDataStateResetCompleted}
              onDataStateChangedCallback={() => {
                const allData = clientGridRef.current
                  ? clientGridRef.current.GetAllGridData()
                  : [];

                if (allData) {
                  determineMainGridColumnsAndAggregateSettings(allData);
                }
              }}
              enableExportAggregates={true}
            >
              {mainGridColumns.length > 0
                ? mainGridColumns.map((singleColumn) =>
                    CreateGridColumn(singleColumn)
                  )
                : null}
            </SharedMainSelectableClientGrid>
          </Col>
        </Row>
      </div>
      <IgnoreModal
        title="Ignore Count"
        show={showIgnoreCount}
        handleClose={() => {
          setShowIgnoreCount(false);
        }}
      >
        <Container>
          <Row>
            You have selected to ignore {getSelectedDetailsCount()} claims. Do
            you wish to continue?
          </Row>

          <br />
          <Row>
            <Col>
              <Button
                variant="secondary"
                onClick={() => setShowIgnoreCount(false)}
              >
                Cancel
              </Button>
            </Col>
            <Col>
              <Button
                className="primary-button"
                onClick={handleIgnoreCountClick}
              >
                Ok
              </Button>
            </Col>
          </Row>
        </Container>
      </IgnoreModal>
      <DetailsModal
        title="Payer Summary"
        show={ShouldDisplayDetailsModal(showModal, showIgnoreModal)}
        handleClose={() => {
          return (
            setShowModal(false),
            setSummaryDetailsGridData(null),
            _container.current.focus()
          );
        }}
      >
        {/*
        NOTE: So there's a different "error toast display"
        being used in SharedMainSelectableServerGrid, see if that suffices.
        I sort of think that maybe we need to change how the ErrorHandler component
        displays values?

        The ErrorHandler had been a way of "displaying errors", but for at least some of this
        it's being handled by the "grid where the data should be displayed".

        Should we continue enforcing the pattern of "show error at the top of the screen",
        or should we just "replace the contents of the grid"?
        */}
        <ErrorHandler
          error={detailError}
          onClose={() => {
            setDetailError({});
          }}
        />
        {summaryDetailsGridData ? (
          <div>
            <Row>
              <Col className="tight-grid multi-line-filter">
                <Grid
                  style={{
                    maxHeight: "10em",
                  }}
                  data={summaryDetailsGridData}
                >
                  {/* {detailsSummaryData &&
                      detailsSummaryData[0] &&
                      detailsSummaryData[0].Payer ? (
                      <GridColumn
                        key={"Payer"}
                        field={"Payer"}
                        title={"Payer"}
                        width={DefaultColumnWidth()}
                      />
                    ) : null} */}

                  <GridColumn
                    key={"ClaimCount"}
                    field={"ClaimCount"}
                    title={"Claim Count"}
                    width={DefaultColumnWidth()}
                  />
                  <GridColumn
                    key={"Adjudicated"}
                    field={"Adjudicated"}
                    title={"Adjudicated"}
                    width={DefaultColumnWidth()}
                    cells={{ data: MoneyCell }}
                  />
                  <GridColumn
                    key={"Paid"}
                    field={"Paid"}
                    title={"Paid"}
                    width={DefaultColumnWidth()}
                    cells={{ data: MoneyCell }}
                  />
                  <GridColumn
                    key={"Outstanding"}
                    field={"Outstanding"}
                    title={"Outstanding"}
                    width={DefaultColumnWidth()}
                    cells={{ data: MoneyCell }}
                  />
                </Grid>
              </Col>
            </Row>
          </div>
        ) : null}
        <br />
        <h2>Aging Detail</h2>

        <h4>{detailsGridTitle}</h4>
        <div>
          {/* 
          NOTE: Using the ZIndexContext.Provider here, we fix the z-index used for "layering" the Kendo grid
          in relation to the Bootstrap modal. This allows the filtering dropdowns to display and work as intended.
          */}
          <ZIndexContext.Provider value={10003}>
            <Row>
              <Col className="tight-grid multi-line-filter">
                <SharedMainSelectableServerGrid
                  ref={agingDetailsServerGridRef}
                  aggregateColumnSettings={null}
                  sendAggregatesParentCallback={() => {}}
                  sendSelectablesParentCallback={() => {}}
                  exportFileNamePrefix={"CurrentAgingDetails"}
                  apiGetEndpoint={detailsGridApiEndpoint}
                  apiPostEndpoint={detailsGridApiPostEndpoint}
                  apiSubmittedFormData={detailsGridParameters}
                  gridErrorHandlerCallback={detailGridErrorCallback}
                  dataItemKey={DETAILS_DATA_ITEM_KEY}
                  secondsBeforeLoadingSpinner={5}
                  //NOTE TODO: This "refreshGrid" prop does nothing, remove it
                  refreshGrid={refreshDetailGrid}
                  useQueryOptions={detailsGridUseQueryOptions}
                  gridButtons={gridButtons}
                  isSelectable={true}
                  // onGridIsLoadingStatusChanged={(isServerGridDataLoading) => {
                  //   SetSummaryDetailsDataHelper(
                  //     isServerGridDataLoading,
                  //     agingDetailsServerGridRef,
                  //     setSummaryDetailsGridData
                  //   )
                  // }}
                  onGridApiDataLoadedCallback={() => {
                    SetSummaryDetailsDataHelper(
                      agingDetailsServerGridRef,
                      setSummaryDetailsGridData
                    );
                  }}
                >
                  <GridColumn
                    key={""}
                    field={""}
                    title={"Ignore"}
                    width={"100em"}
                    filterable={false}
                    cells={{
                      data: CustomIgnoreDetailCell,
                    }}
                  />
                  <GridColumn
                    key={"ncpdp"}
                    field={"ncpdp"}
                    title={"NCPDP"}
                    width={DefaultBooleanOrShortWidthColumnWidth()}
                  />
                  <GridColumn
                    key={"rxNumber"}
                    field={"rxNumber"}
                    title={"Rx #"}
                    filter={"text"}
                    width={DefaultColumnWidth()}
                  />
                  <GridColumn
                    key={"dispensedDate"}
                    field={"dispensedDate"}
                    title={"Dispensed Date"}
                    filter={"date"}
                    format="{0:d}"
                    width={DefaultDateColumnWidth()}
                  />
                  <GridColumn
                    key={"transactionDate"}
                    field={"transactionDate"}
                    title={"Transaction Date"}
                    filter={"date"}
                    format="{0:d}"
                    width={DefaultDateColumnWidth()}
                  />
                  {selectedDetailsPropsForRefetchAfterIgnore.isAggregate ? (
                    <GridColumn
                      key={"payerName"}
                      field={"payerName"}
                      title={"Payer Name"}
                      width={DefaultColumnWidth()}
                    />
                  ) : null}
                  <GridColumn
                    key={"bin"}
                    field={"bin"}
                    title={"BIN"}
                    width={DefaultColumnWidth()}
                  />
                  <GridColumn
                    key={"pcn"}
                    field={"pcn"}
                    title={"PCN"}
                    width={DefaultColumnWidth()}
                  />
                  <GridColumn
                    key={"promiseToPay"}
                    field={"promiseToPay"}
                    title={"Promise to Pay"}
                    filter={"numeric"}
                    width={DefaultColumnWidth()}
                    cells={{
                      data: MoneyCell,
                      footerCell: AggregateMoneyDetailFooterCell,
                    }}
                    footerCell={AggregateMoneyDetailFooterCell}
                    headerCell={PromiseToPayHeaderCell}
                  />
                  <GridColumn
                    key={"remittancePaid"}
                    field={"remittancePaid"}
                    title={"Paid"}
                    filter={"numeric"}
                    width={DefaultColumnWidth()}
                    cells={{
                      data: MoneyCell,
                      footerCell: AggregateMoneyDetailFooterCell,
                    }}
                    footerCell={AggregateMoneyDetailFooterCell}
                  />
                  <GridColumn
                    key={"outstanding"}
                    field={"outstanding"}
                    title={"Outstanding"}
                    filter={"numeric"}
                    width={DefaultColumnWidth()}
                    cells={{
                      data: MoneyCell,
                      footerCell: AggregateMoneyDetailFooterCell,
                    }}
                    footerCell={AggregateMoneyDetailFooterCell}
                  />
                  <GridColumn
                    key={"claimNumber"}
                    field={"claimNumber"}
                    title={"Claim #"}
                    width={DefaultColumnWidth()}
                  />
                  <GridColumn
                    key={"cardholderId"}
                    field={"cardholderId"}
                    title={"Cardholder ID"}
                    width={DefaultColumnWidth()}
                  />
                  <GridColumn
                    key={"adjudicatedCoPay"}
                    field={"adjudicatedCoPay"}
                    title={"Copay"}
                    filter={"numeric"}
                    width={DefaultColumnWidth()}
                    cells={{
                      data: MoneyCell,
                      footerCell: AggregateMoneyDetailFooterCell,
                    }}
                    footerCell={AggregateMoneyDetailFooterCell}
                  />
                </SharedMainSelectableServerGrid>
              </Col>
            </Row>
          </ZIndexContext.Provider>
        </div>
      </DetailsModal>
      <IgnoreModal
        title="Ignore Details"
        show={showIgnoreModal}
        handleClose={() => {
          return (
            setShowIgnoreModal(false),
            setIgnoreMultipleSelectedClaimsInDetailModal(false)
          );
        }}
      >
        <ErrorHandler
          error={IgnoreError}
          onClose={() => {
            setIgnoreError({});
          }}
        />
        <IgnoreBody
          selectedNcpdpIgnoreModalText={selectedNcpdpIgnoreModalText}
          selectedRxIgnoreModalText={selectedRxIgnoreModalText}
          handleCancelClick={handleCancelIgnoreClick}
          selectedClidsToIgnore={clidsToIgnore}
          onSaveButtonClickCallbackFromParent={onIgnoreClaimsSaveButtonCallback}
          onSuccessCallbackFromParent={onIgnoreClaimSuccessCallback}
          disableControls={isIgnoreClaimsFormDisabled}
        ></IgnoreBody>
      </IgnoreModal>
    </div>
  );
}
